// @ts-check
/** @jsx jsx */
/** @jsxFrag React.Fragment */
import asMyList from '@bottlebooks/gatsby-plugin-california-pdf/src/components/asMyList.js';
import MyList from '@bottlebooks/gatsby-plugin-california-pdf/src/templates/MyList/MyList';
import useUser from '@bottlebooks/gatsby-plugin-firebase-auth/src/useUser';
import useUserProfile from '@bottlebooks/gatsby-plugin-firebase-auth/src/useUserProfile';
import useTastingNoteExport from '@bottlebooks/gatsby-plugin-tastingnotes/src/useTastingNoteExport';
import useExportAllProducts from '@bottlebooks/gatsby-plugin-tastingnotes/src/useExportAllProducts';
import {
  Dialog,
  DownloadIcon,
  Link,
  Stack,
  Text,
} from '@bottlebooks/gatsby-theme-base/src';
import { useCollection } from '@bottlebooks/gatsby-theme-event/src/components/SiteSearch/useEventData';
import useLocale from '@bottlebooks/gatsby-theme-event/src/useLocale';
import { Trans } from '@lingui/macro';
import '@reach/dialog/styles.css';
import { PDFDownloadLink } from '@react-pdf/renderer';
import React from 'react';
import { jsx } from 'theme-ui';
import { useFavorites } from '..';
import useMyListQuery from './useMyListQuery';
import useSiteConfig from '@bottlebooks/gatsby-theme-event/src/components/useSiteConfig';

/**
 * @param {{ onClose: () => void;}} props
 */
export default function CatalogExportDialog({ onClose }) {
  return (
    <Dialog
      onDismiss={onClose}
      aria-label="Catalog export dialog"
      sx={{
        position: 'relative',
        width: ['100vw', 600],
        marginTop: [0, '10vh'],
        height: ['100vh', 'auto'],
      }}
    >
      <Dialog.Header onDismiss={onClose}>
        <Trans>Download your personal tasting booklet</Trans>
      </Dialog.Header>
      <Dialog.Content>
        <Text sx={{ marginBottom: 3 }}>
          <Trans>
            Your personal tasting booklet contains all the wines you have
            bookmarked, rated, or written tasting notes for.
          </Trans>
        </Text>
        <CatalogExport />
      </Dialog.Content>
    </Dialog>
  );
}

function CatalogExport() {
  const { collectionId } = useCollection();
  const { listsEnabled } = useSiteConfig();
  const { isLoading: isLoadingFavorites, favorites } = useFavorites();
  const { userProfile } = useUserProfile();
  const { exportAsExcel: exportTastingNotes } = useTastingNoteExport();
  const { exportAllProductsAsExcel } = useExportAllProducts();
  if (isLoadingFavorites) return null;
  if (favorites?.length === 0)
    return (
      <Text sx={{ color: 'brand.warningRed' }}>
        <Trans>
          You have not added any exhibitors or products to your list. After you
          add items to your list, come back and your personal tasting booklet
          will be ready to download.
        </Trans>
      </Text>
    );

  return (
    <Stack justify="flex-end" sx={{ marginRight: [3, 0] }}>
      {listsEnabled ? (
        <Text>
          <Link
            href={`https://app.bottlebooks.me/pub/next/collections/${collectionId}/tastinglists/users/${userProfile?.uid}?inline=1`}
            target="_blank"
          >
            <DownloadIcon sx={{ marginRight: 2 }} />
            <Trans>Download my tasting lists as PDF</Trans>
          </Link>
        </Text>
      ) : null}

      <DownloadMyTastingBooklet />

      <Text>
        <Link onClick={exportTastingNotes}>
          <DownloadIcon sx={{ marginRight: 2 }} />
          <Trans>Download tasting booklet as Excel</Trans>
        </Link>
      </Text>
      <Text>
        <Link onClick={exportAllProductsAsExcel}>
          <DownloadIcon sx={{ marginRight: 2 }} />
          <Trans>Download all products as Excel</Trans>
        </Link>
      </Text>
    </Stack>
  );
}

export function DownloadMyTastingBooklet({ fileName = 'my-list.pdf' }) {
  const [user] = useUser();
  const locale = useLocale();
  const { collectionId } = useCollection();
  const template = asMyList(MyList);
  const { loading, error, data } = useMyListQuery({
    query: template?.query,
    collectionId,
    locale,
  });
  if (loading) {
    return (
      <Text sx={{ color: 'primary' }}>
        <DownloadIcon sx={{ marginRight: 2 }} />{' '}
        <Trans>Download my tasting booklet as PDF</Trans> –{' '}
        <Trans>Loading data…</Trans>
      </Text>
    );
  }
  if (error) {
    console.error(error);
    return (
      <Text>
        <Trans>There was an error loading your list data.</Trans>
      </Text>
    );
  }
  const Document = template.layout;
  return (
    <PDFDownloadLink
      sx={{ variant: 'link.text' }}
      document={
        <Document
          data={data}
          collectionId={collectionId}
          locale={locale}
          user={user}
        />
      }
      fileName={fileName}
    >
      {({ blob, error }) => {
        if (error) {
          return (
            <Text>
              <Trans>There was an error creating your file.</Trans>
            </Text>
          );
        }
        if (!blob) {
          return (
            <Text sx={{ color: 'primary' }}>
              <DownloadIcon sx={{ marginRight: 2 }} />{' '}
              <Trans>Download as PDF</Trans> –{' '}
              <Trans>Creating your list…</Trans>
            </Text>
          );
        }
        return (
          <Text sx={{ variant: 'link.text', color: 'primary' }}>
            <DownloadIcon sx={{ marginRight: 2 }} />{' '}
            <Trans>Download as PDF</Trans>
          </Text>
        );
      }}
    </PDFDownloadLink>
  );
}
